/**
 * DSL Helper Functions
 * 
 * Signal Direction Detection:
 * 
 * The system automatically detects signal direction (long/short) using multiple methods,
 * in order of priority:
 * 
 * 1. EXPLICIT MARKERS (Recommended - Highest Priority):
 *    Use @buy, @sell, @long, or @short in comments:
 * 
 *    # @buy
 *    signal fast_breakout = crossover(sma(close, 11), sma(close, 26))
 * 
 *    # @sell
 *    signal fast_breakout2 = crossunder(sma(close, 11), sma(close, 26))
 * 
 *    Or inline:
 *    signal entry = high > high[1] # @buy
 * 
 * 2. Comment keywords on same line:
 *    signal entry = high > high[1] # buy
 * 
 * 3. Comment keywords on previous line:
 *    # buy
 *    signal entry = high > high[1]
 * 
 * 4. Signal name patterns (guessing):
 *    signal long_entry = ...
 *    signal short_entry = ...
 * 
 * Note: Guessing only uses signal names and comments, NOT DSL logic patterns
 */

/**
 * Check if DSL code contains signal definitions
 */
export function hasSignals(dslCode: string): boolean {
  const signalRegex = /\bsignal\s+\w+\s*=/gi;
  return signalRegex.test(dslCode);
}

/**
 * Extract signal names from DSL code
 */
export function extractSignalNames(dslCode: string): string[] {
  const signalRegex = /\bsignal\s+(\w+)\s*=/gi;
  const matches = [];
  let match;

  while ((match = signalRegex.exec(dslCode)) !== null) {
    matches.push(match[1]);
  }

  return matches;
}

/**
 * Check if DSL code is valid for signal analysis
 * 
 * Note: Works with any output type (stats, table, signals) as long as the script
 * contains at least one variable marked with 'signal' keyword.
 * The backend automatically handles output type conversion for analysis.
 */
export function isValidForSignalAnalysis(dslCode: string): boolean {
  return dslCode.trim().length > 0 && hasSignals(dslCode);
}

/**
 * Detect signal direction (long or short) based on:
 * 1. Explicit markers in comments (@buy, @sell, @long, @short) - HIGHEST PRIORITY
 * 2. Comment on same line (e.g., # buy, // long)
 * 3. Comment on previous line (e.g., # buy)
 * 4. Signal name patterns (guessing only - e.g., long_entry, short_entry)
 * 
 * Note: Guessing only uses signal names and comments, NOT DSL logic patterns
 * 
 * Explicit marker format (recommended):
 *   # @buy
 *   signal entry = high > high[1]
 * 
 *   # @sell
 *   signal exit = low < low[1]
 * 
 * Or inline:
 *   signal entry = high > high[1] # @buy
 */
function detectSignalDirection(signalName: string, dslCode: string): 'long' | 'short' | 'unknown' {
  const nameLower = signalName.toLowerCase();

  // Patterns for SHORT signals (in names and comments)
  const shortPatterns = [
    /short|sell|down|bear|exit_short|short_entry|short_exit/i,
    /crossunder|below|under/i
  ];

  // Patterns for LONG signals (in names and comments)
  const longPatterns = [
    /long|buy|up|bull|entry_long|long_entry|long_exit/i,
    /crossover|above|over/i
  ];

  /**
   * Check for explicit direction markers (@buy, @sell, @long, @short)
   * These have the highest priority and are the recommended way to specify direction
   */
  const checkExplicitMarker = (comment: string): 'long' | 'short' | null => {
    const commentLower = comment.toLowerCase().trim();
    
    // Explicit markers: @buy, @sell, @long, @short
    if (commentLower.includes('@sell') || commentLower.includes('@short')) {
      return 'short';
    }
    if (commentLower.includes('@buy') || commentLower.includes('@long')) {
      return 'long';
    }
    
    return null;
  };

  /**
   * Check for direction keywords in comments (fallback)
   * Used when explicit markers are not found
   */
  const checkCommentForDirection = (comment: string): 'long' | 'short' | null => {
    const commentLower = comment.toLowerCase();
    if (shortPatterns.some(p => p.test(commentLower))) {
      return 'short';
    }
    if (longPatterns.some(p => p.test(commentLower))) {
      return 'long';
    }
    return null;
  };

  // Find the line with the signal declaration
  const lines = dslCode.split('\n');
  const signalLineIndex = lines.findIndex(line => 
    new RegExp(`signal\\s+${signalName}\\s*=`).test(line)
  );
  
  if (signalLineIndex >= 0) {
    const signalLine = lines[signalLineIndex];
    
    // 1. Check explicit markers in comment on same line (HIGHEST PRIORITY)
    // Pattern: signal entry = high > high[1] # @buy
    // or: signal entry = high > high[1] // @sell
    const inlineCommentRegex = new RegExp('(?:#|//)\\s*(.+)$');
    const inlineCommentMatch = signalLine.match(inlineCommentRegex);
    if (inlineCommentMatch && inlineCommentMatch[1]) {
      // First check for explicit markers
      const explicitDirection = checkExplicitMarker(inlineCommentMatch[1]);
      if (explicitDirection) {
        return explicitDirection;
      }
      // Fallback to keyword detection
      const direction = checkCommentForDirection(inlineCommentMatch[1]);
      if (direction) {
        return direction;
      }
    }
    
    // 2. Check explicit markers in comment on previous line (HIGH PRIORITY)
    if (signalLineIndex > 0) {
      // Check previous lines for comments (up to 3 lines back)
      for (let i = signalLineIndex - 1; i >= Math.max(0, signalLineIndex - 3); i--) {
        const line = lines[i].trim();
        
        // Check if line is a comment (# or //)
        const commentMatch = line.match(/^[#/]{1,2}\s*(.+)/);
        if (commentMatch && commentMatch[1]) {
          // First check for explicit markers
          const explicitDirection = checkExplicitMarker(commentMatch[1]);
          if (explicitDirection) {
            return explicitDirection;
          }
          // Fallback to keyword detection
          const direction = checkCommentForDirection(commentMatch[1]);
          if (direction) {
            return direction;
          }
        }
        
        // If we hit a non-empty, non-comment line, stop searching
        if (line && !line.match(/^[#/]/)) {
          break;
        }
      }
    }
  }

  // 3. Check signal name patterns (automatic detection/guessing)
  // Examples: long_entry, short_entry, buy_signal, sell_signal
  // Only names and comments are used for guessing, not DSL logic
  if (shortPatterns.some(p => p.test(nameLower))) {
    return 'short';
  }
  if (longPatterns.some(p => p.test(nameLower))) {
    return 'long';
  }

  // Guessing only uses names and comments, not DSL logic patterns
  return 'unknown';
}

/**
 * Automatically detect signal directions from DSL code
 * 
 * Supports explicit markers (@buy, @sell, @long, @short) and automatic detection
 * based on signal names, comments, and DSL logic.
 * 
 * @param dslCode - The DSL code containing signal definitions
 * @returns A mapping of signal name to direction (long or short)
 * 
 * @example
 * ```dsl
 * # @buy
 * signal fast_breakout = crossover(sma(close, 11), sma(close, 26))
 * 
 * # @sell
 * signal fast_breakout2 = crossunder(sma(close, 11), sma(close, 26))
 * ```
 * 
 * Returns: { fast_breakout: 'long', fast_breakout2: 'short' }
 */
export function detectSignalDirections(dslCode: string): Record<string, 'long' | 'short'> {
  const directions: Record<string, 'long' | 'short'> = {};

  // Extract signal names
  const signalNames = extractSignalNames(dslCode);

  for (const signalName of signalNames) {
    const direction = detectSignalDirection(signalName, dslCode);
    if (direction !== 'unknown') {
      directions[signalName] = direction;
    }
  }

  return directions;
}